import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FileText, Heart, BarChart, MessageSquare, History, Brain, Wand, Upload, Pencil, Check, X, Send, ClipboardCopy, Sparkles, Globe, Mail, CheckSquare2, Inbox, Users2 } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Eye, ChevronDown } from "lucide-react";
import { JobDetailHeader } from "@/components/jobs/JobDetailHeader";
import { JobProfile } from "@/components/jobs/JobProfile";
import { TopMatchesCard } from "@/components/jobs/TopMatchesCard";
import { JobDetailSkeleton } from "@/components/jobs/JobDetailSkeleton";
import { JobNotFound } from "@/components/jobs/JobNotFound";
import { JobKanban } from "@/components/jobs/JobKanban";
import { useJobDetail } from "@/hooks/jobs/useJobDetail";
import { JobAnalysisCard } from "@/components/jobs/JobAnalysisCard";
import { useSettings } from "@/providers/SettingsProvider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DynamicTabBar, Tab } from "@/components/ui/DynamicTabBar";
import { JobComments } from "@/components/jobs/JobComments";
import { JobHistory } from "@/components/jobs/JobHistory";
import { JobDocumentsTab } from "@/components/jobs/JobDocumentsTab";
import { JobAssignmentsTab } from "@/components/jobs/JobAssignmentsTab";
import { EntityMailbox } from "@/components/inbox/EntityMailbox";
import ReactMarkdown from 'react-markdown';
import { supabase } from "@/integrations/supabase/client";
import { useJobTopMatches } from "@/hooks/jobs/useJobTopMatches";
import { useTranslation } from "react-i18next";
import { JobAnalysisTab } from "@/components/jobs/JobAnalysisTab";
import { JobTasksTab } from "@/components/jobs/JobTasksTab";
import { AIAnalysisProgress } from "@/components/ui/AIAnalysisProgress";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'isomorphic-dompurify';
import { marked } from 'marked';
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { JobProfilesTab } from "@/components/jobs/JobProfilesTab";
import { JOB_STATUS_VALUES } from "@/components/jobs/types/jobTypes";
import { JobClosedWonWorkforceModal } from "@/components/jobs/JobClosedWonWorkforceModal";

import { fetchCustomFields, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useTrackPageVisit } from "@/hooks/useEntityHistory";

interface AnalysisData {
	summary?: string;
	highlights?: string[];
	skills_required?: string[];
	hard_skills?: {
		explicit?: string[];
		implicit?: string[];
	};
	work_culture?: {
		team_orientation?: number;
		formality?: number;
		work_life_balance?: number;
		notes?: string;
	};
	workload?: {
		intensity?: number;
		overtime_expectation?: number;
		notes?: string;
	};
	innovation?: {
		change_frequency?: number;
		creativity_emphasis?: number;
		notes?: string;
	};
	management?: {
		autonomy?: number;
		feedback_style?: string;
		notes?: string;
	};
	compensation?: {
		mentioned_elements?: string[];
		competitiveness_indicators?: string[];
		notes?: string;
	};
	remote_work?: {
		flexibility?: number;
		notes?: string;
	};
	key_strengths?: string[];
	potential_concerns?: string[];
	overall_recommendation?: string;
	[key: string]: any;
}

const CustomQuillToolbar = () => (
	<div id="custom-quill-toolbar" className="flex items-center w-full border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1">
		<div className="flex items-center gap-1">
			<select className="ql-header" defaultValue="" onChange={e => e.persist()}>
				<option value="1"></option>
				<option value="2"></option>
				<option value=""></option>
			</select>
			<button className="ql-bold" />
			<button className="ql-italic" />
			<button className="ql-underline" />
			<button className="ql-strike" />
			<button className="ql-list" value="ordered" />
			<button className="ql-list" value="bullet" />
			<button className="ql-link" />
			<button className="ql-clean" />
		</div>
	</div>
);

// Helper to copy job data to jobs_public after publishing
async function copyJobToPublicFromJobData(jobData, orgName = '', orgId = '') {
	//console.log('[DEBUG] copyJobToPublicFromJobData called with:', jobData, orgName, orgId);
	if (!jobData) {
		console.error('[DEBUG] No jobData provided to copyJobToPublicFromJobData');
		return null;
	}
	if (!jobData.JobDesc_Public || !jobData.JobDesc_Public.trim()) {
		console.warn('[DEBUG] JobDesc_Public is empty or null, will not upsert to jobs_public:', jobData);
		// return null;
	}
	const publicRecord = {
		job_id: jobData.id,
		title: jobData.title || '',
		jobdesc_public: jobData.JobDesc_Public || '',
		org_name: orgName || '',
		org_id: jobData.organization_uuid || orgId || '',
	};
	//console.log('[DEBUG] Upserting to jobs_public:', publicRecord);
	const { data: upserted, error: upsertError } = await supabase
		.from("jobs_public")
		.upsert([publicRecord], { onConflict: 'job_id' });
	if (upsertError) {
		console.error("[DEBUG] jobs_public upsert error:", upsertError, publicRecord);
		return null;
	}
	//console.log('[DEBUG] Upserted result:', upserted);
	return (upserted && upserted[0]) ? upserted[0] : publicRecord;
}

const JobDetail = () => {
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const { job, loading, formattedDate, refetch } = useJobDetail(id);
	const { matches, loading: matchesLoading, error: matchesError } = useJobTopMatches(id, 10);
	const { dateFormat, timeFormat } = useSettings();

	usePageTitle(job ? job.title : "Job");

	const [activeTab, setActiveTab] = useState("process");
	const { t } = useTranslation();
	const { currentOrganization, user } = useAuth();
	const { toast } = useToast();

	const jobTabConfig: Tab[] = useMemo(() => [
		{ id: "process", label: t("candidates.tabs.recruitmentProcess"), icon: <BarChart className="h-4 w-4" /> },
		{ id: "description", label: t("jobs.formWizard.jobDescription"), icon: <FileText className="h-4 w-4" /> },
		{ id: "public", label: "Public", icon: <Globe className="h-4 w-4" /> },
		{ id: "ai-analysis", label: t("candidates.tabs.aiAnalysis"), icon: <Brain className="h-4 w-4" /> },
		{ id: "documents", label: t("candidates.tabs.documents", "Documents"), icon: <Upload className="h-4 w-4" /> },
		{ id: "assignments", label: t("jobs.tabs.assignments", "Assignments"), icon: <Users2 className="h-4 w-4" /> },
		{ id: "tasks", label: t("jobs.tabs.tasks", "Tasks"), icon: <CheckSquare2 className="h-4 w-4" /> },
		{ id: "comments", label: t("candidates.tabs.comments"), icon: <MessageSquare className="h-4 w-4" /> },
		{ id: "history", label: t("candidates.tabs.history"), icon: <History className="h-4 w-4" /> },
		{ id: "mailbox", label: t("jobs.tabs.mailbox", "Mailbox"), icon: <Inbox className="h-4 w-4" /> },
	], [t]);

	// Switch to tasks tab if task parameter is present
	useEffect(() => {
		const taskId = searchParams.get('task');
		if (taskId && activeTab !== 'tasks') {
			setActiveTab('tasks');
		}
	}, [searchParams]);

	// Use a ref to track the initial load to prevent multiple refreshes
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);
	const [isFormatting, setIsFormatting] = useState(false);
	const [isEditingPublic, setIsEditingPublic] = useState(false);
	const [publicDescDraft, setPublicDescDraft] = useState("");
	const [isSavingPublic, setIsSavingPublic] = useState(false);
	const [isEditingOriginal, setIsEditingOriginal] = useState(false);
	const [originalDescDraft, setOriginalDescDraft] = useState("");
	const [isSavingOriginal, setIsSavingOriginal] = useState(false);
	const [rewritingWithAI, setRewritingWithAI] = useState(false);
	const [showRewriteModal, setShowRewriteModal] = useState(false);
	const [rewriteInstructions, setRewriteInstructions] = useState("");
	const [rewriteError, setRewriteError] = useState("");
	const rewriteButtonRef = useRef<HTMLButtonElement>(null);
	const [showPublishModal, setShowPublishModal] = useState(false);
	const [publishOptions, setPublishOptions] = useState({
		publicPage: true,
		orgWebsite: false,
	});
	const [isPublishing, setIsPublishing] = useState(false);
	const [isPublishingJob, setIsPublishingJob] = useState(false);
	const [customFields, setCustomFields] = useState([]);
	const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
	const [customFieldsError, setCustomFieldsError] = useState<string | null>(null);
	const [isEditingJob, setIsEditingJob] = useState(false);
	const [inlineStatusValue, setInlineStatusValue] = useState("");
	const [isUpdatingInlineStatus, setIsUpdatingInlineStatus] = useState(false);
	const [showClosedWonModal, setShowClosedWonModal] = useState(false);
	const [kanbanRefreshKey, setKanbanRefreshKey] = useState(0);
	const organizationId = currentOrganization?.id;

	useEffect(() => {
		setInlineStatusValue(job?.status?.toLowerCase() || '');
	}, [job?.status]);

	// Track page visit for history
	useTrackPageVisit('job', job?.id, job?.title, {
		companyName: job?.company?.name
	});

	// Refresh data only on initial load or when explicitly triggered by the header component
	useEffect(() => {
		if (id && refetch && !initialLoadComplete) {
			//console.log("Job detail page - initial data load for job ID:", id);
			refetch().then(() => {
				setInitialLoadComplete(true);
			});
		}
	}, [id, refetch, initialLoadComplete]);

	useEffect(() => {
		if (!organizationId) return;
		setCustomFieldsLoading(true);
		setCustomFieldsError(null);
		fetchCustomFields(organizationId)
			.then((data: CustomFieldsJSON) => {
				setCustomFields(data.Jobs || []);
			})
			.catch(() => {
				setCustomFieldsError("Failed to load custom fields");
			})
			.finally(() => setCustomFieldsLoading(false));
	}, [organizationId]);

	// Load specific tab content only when the tab is active to improve performance
	const loadTabContent = (tabValue: string) => {
		return tabValue === activeTab;
	};

	if (loading && !initialLoadComplete) {
		return <JobDetailSkeleton />;
	}

	if (!job) {
		return <JobNotFound />;
	}

	let analysisData: AnalysisData | undefined;
	if (typeof job.ai_analysis === 'string') {
		try {
			analysisData = job.ai_analysis ? JSON.parse(job.ai_analysis) : undefined;
		} catch (error) {
			console.error("Error parsing job analysis:", error);
			analysisData = undefined;
		}
	} else {
		analysisData = job.ai_analysis as AnalysisData | undefined;
	}

	const handleImproveClick = async () => {
		
		if (!job?.description) {
			//console.log("No job description available to improve.");
			return;
		}

		setIsFormatting(true);

		try {
			const { data, error } = await supabase.functions.invoke("reformat-text-with-ai", {
				body: { text: job.description },
			});

			if (error) {
				console.error("Error invoking edge function:", error.message);
				return;
			}

			//console.log("Improved description:", data.text);

			// if this is a sucess, we can update the supabase table with the new description
			const { error: updateError } = await supabase
				.from("jobs")
				.update({ description: data.text })
				.eq("id", id);

			if (updateError) {
				console.error("Error updating job description:", updateError.message);
			}
			// Refetch the job details to get the updated description
			await refetch();
			
		} catch (err) {
			console.error("Unexpected error:", err);
		} finally {
			setIsFormatting(false);
		}
	};

	const handleEditPublicClick = () => {
		setPublicDescDraft(job.JobDesc_Public || "");
		setIsEditingPublic(true);
	};

	const handleCancelEditPublic = () => {
		setIsEditingPublic(false);
	};

	const handleEditOriginalClick = () => {
		setIsEditingOriginal(true);
		setOriginalDescDraft(job.description || "");
	};

	const handleCancelEditOriginal = () => {
		setIsEditingOriginal(false);
		setOriginalDescDraft("");
	};

	const handleSaveOriginal = async () => {
		if (!job?.id) return;

		setIsSavingOriginal(true);
		try {
			const { error } = await supabase
				.from('jobs')
				.update({ description: originalDescDraft })
				.eq('id', job.id);

			if (error) {
				console.error('Error saving original description:', error);
				toast({
					title: t("common.error", "Error"),
					description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + error.message,
					variant: "destructive",
				});
			} else {
				toast({
					title: t("common.success", "Success"),
					description: t("jobs.detail.updateSuccess", "Job offer updated successfully"),
				});
				setIsEditingOriginal(false);
				await refetch();
			}
		} catch (error) {
			console.error('Error saving original description:', error);
			toast({
				title: t("common.error", "Error"),
				description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + (error instanceof Error ? error.message : t("common.unknownError", "Unknown error")),
				variant: "destructive",
			});
		} finally {
			setIsSavingOriginal(false);
		}
	};

	const handleSavePublic = async () => {
		if (!id) return;
		setIsSavingPublic(true);
		try {
			const { error } = await supabase
				.from("jobs")
				.update({ JobDesc_Public: publicDescDraft })
				.eq("id", id);
			if (error) {
				console.error("Error updating public job description:", error.message);
			} else {
				// Always upsert to jobs_public if the job is published
				const { data: latestJob, error: fetchError } = await supabase
					.from("jobs")
					.select("id, title, JobDesc_Public, organization_uuid, published")
					.eq("id", id)
					.single();
				//console.log('[DEBUG] Fetched latest job after save public:', latestJob, fetchError);
				if (!fetchError && latestJob && latestJob.published) {
					const publicJob = await copyJobToPublicFromJobData(latestJob, currentOrganization?.name, currentOrganization?.id);
					if (publicJob) {
						//console.log("Updated public job after saving public description:", publicJob);
					}
				}
				await refetch();
				setIsEditingPublic(false);
			}
		} catch (err) {
			console.error("Unexpected error saving public job description:", err);
		} finally {
			setIsSavingPublic(false);
		}
	};

	const handleRewriteWithAI = () => {
		setRewriteInstructions("");
		setRewriteError("");
		setShowRewriteModal(true);
	};

	const handleSubmitRewrite = async () => {
		setRewritingWithAI(true);
		setRewriteError("");
		try {
			const { data, error } = await supabase.functions.invoke("rewrite-public-job-description", {
				body: {
					currentText: publicDescDraft,
					originalText: job.description || "",
					instructions: rewriteInstructions,
				},
			});
			if (error || !data || !data.rewritten) {
				setRewriteError(error?.message || "AI rewrite failed.");
			} else {
				setPublicDescDraft(data.rewritten);
				setShowRewriteModal(false);
			}
		} catch (err) {
			setRewriteError("Unexpected error: " + (err.message || err));
		} finally {
			setRewritingWithAI(false);
		}
	};

	const handleJobEdit = () => {
		setIsEditingJob(true);
	};

	const handleJobSave = () => {
		setIsEditingJob(false);
	};

	const handleJobCancel = () => {
		setIsEditingJob(false);
	};

	const handleInlineStatusChange = async (nextStatus: string) => {
		if (!id || nextStatus === inlineStatusValue)
			return;

		const previousStatus = inlineStatusValue;
		setInlineStatusValue(nextStatus);
		setIsUpdatingInlineStatus(true);

		try {
			const { error } = await supabase
				.from('jobs')
				.update({ status: nextStatus })
				.eq('id', id);

			if (error)
				throw error;

			// When job is closed (won or lost), close all open matches and unpublish
			if (nextStatus === 'closed/won' || nextStatus === 'closed/lost') {
				// For closed/won, don't close matches yet — the modal will handle hired match
				if (nextStatus === 'closed/lost')
					await supabase.rpc('set_match_application_closed', { p_job_id: id });

				await supabase
					.from("jobs")
					.update({ published: false, publishdate: null })
					.eq("id", id);
				await supabase
					.from("jobs_public")
					.delete()
					.eq("job_id", id);
				setKanbanRefreshKey(k => k + 1);
			}

			await refetch();

			toast({
				title: t("common.success", "Success"),
				description: t("jobs.detail.updateSuccess", "Job offer updated successfully"),
			});

			// Show workforce modal for closed/won
			if (nextStatus === 'closed/won')
				setShowClosedWonModal(true);
		} catch (error) {
			setInlineStatusValue(previousStatus);
			toast({
				title: t("common.error", "Error"),
				description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + (error instanceof Error ? error.message : t("common.unknownError", "Unknown error")),
				variant: "destructive",
			});
		} finally {
			setIsUpdatingInlineStatus(false);
		}
	};

	const getInlineJobStatusClasses = (status: string) => {
		switch (status) {
			case 'open':
				return {
					dot: 'bg-blue-500',
					text: 'text-blue-500'
				};
			case 'hold':
				return {
					dot: 'bg-amber-500',
					text: 'text-amber-600'
				};
			case 'closed/won':
				return {
					dot: 'bg-emerald-500',
					text: 'text-emerald-600'
				};
			case 'closed/lost':
				return {
					dot: 'bg-rose-500',
					text: 'text-rose-600'
				};
			default:
				return {
					dot: 'bg-slate-400',
					text: 'text-gray-600 dark:text-gray-300'
				};
		}
	};

	// Job update handler - similar to JobDetailHeader's handleUpdateJob
	const handleUpdateJob = async (updatedJob: any) => {
		if (!id) {
			console.error("No job ID provided for update");
			toast({
				title: t("common.error", "Error"),
				description: t("jobs.detail.noJobId", "Cannot update job: No job ID provided"),
				variant: "destructive",
			});
			return false;
		}

		try {
			// Process the arrays from comma-separated strings
			const skillsArray = updatedJob.skills
				? updatedJob.skills.split(',').map((skill: string) => skill.trim()).filter(Boolean)
				: [];

			const hardSkillsArray = updatedJob.hardSkills
				? updatedJob.hardSkills.split(',').map((skill: string) => skill.trim()).filter(Boolean)
				: [];

			const softSkillsArray = updatedJob.softSkills
				? updatedJob.softSkills.split(',').map((skill: string) => skill.trim()).filter(Boolean)
				: [];

			const cultureTagsArray = updatedJob.cultureTags
				? updatedJob.cultureTags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
				: [];

			// Normalize language requirements
			const languageRequirementsArray = Array.isArray(updatedJob.languageRequirements) 
				? updatedJob.languageRequirements 
				: updatedJob.languageRequirements 
					? [updatedJob.languageRequirements] 
					: [];

			//console.log('handleUpdateJob - updatedJob:', updatedJob);
			// Clean up potential undefined values for database
			const jobUpdateData = {
				title: updatedJob.title || null,
				company_id: updatedJob.companyId || null,
				description: updatedJob.description || null,
				ai_summary: updatedJob.aiSummary || null,
				skills: skillsArray.length > 0 ? skillsArray : null,
				contact_id: updatedJob.contactId || null,
				employment_type: updatedJob.employmentType || null,
				contract_duration: updatedJob.contractDuration || null,
				start_date: updatedJob.startDate || null,
				end_date: updatedJob.endDate || null,
				seniority_level: updatedJob.seniorityLevel || null,
				budget: updatedJob.budget || null,
				budget_freelance: updatedJob.budgetFreelance || null,
				location_type: updatedJob.locationType || null,
				workplace_breakdown: updatedJob.workplaceBreakdown || null,
				hard_skills: hardSkillsArray.length > 0 ? hardSkillsArray : null,
				soft_skills: softSkillsArray.length > 0 ? softSkillsArray : null,
				education_level: updatedJob.educationLevel || null,
				years_of_experience: updatedJob.yearsOfExperience || null,
				culture_tags: cultureTagsArray.length > 0 ? cultureTagsArray : null,
				application_deadline: updatedJob.applicationDeadline || null,
				job_posting_date: updatedJob.jobPostingDate || null,
				source: updatedJob.source || null,
				assigned_recruiter: updatedJob.assignedRecruiter || null,
				employment_classification: updatedJob.employmentClassification || null,
				status: updatedJob.status || null,
				closing_reason: updatedJob.closing_reason || null,
				custom_fields: updatedJob.custom_fields || null
			};

			//console.log('handleUpdateJob - jobUpdateData:', jobUpdateData);

			const { error: updateError } = await supabase
				.from('jobs')
				.update(jobUpdateData)
				.eq('id', id);

			if (updateError) {
				console.error("Error from Supabase:", updateError);
				toast({
					title: t("common.error", "Error"),
					description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + updateError.message,
					variant: "destructive",
				});
				return false;
			}

			toast({
				title: t("common.success", "Success"),
				description: t("jobs.detail.updateSuccess", "Job offer updated successfully"),
			});

			return true;
		} catch (error) {
			console.error("Error updating job:", error);
			toast({
				title: t("common.error", "Error"),
				description: t("jobs.detail.updateFailed", "Failed to update job offer: ") + (error instanceof Error ? error.message : t("common.unknownError", "Unknown error")),
				variant: "destructive",
			});
			return false;
		}
	};

	return (
		<div className="w-full">

            <AIAnalysisProgress 
				entityId={id}
				entityType="job"
				onRefresh={() => {
					refetch();
				}}
			/>

			<JobDetailHeader
				jobId={id}
				refreshData={() => {
					//console.log("Manual refresh triggered from header");
					refetch();
				}}
				onEdit={handleJobEdit}
				isEditing={isEditingJob}
			/>			{/* Job Title */}
			<div className="mb-6">
				<div className="flex items-center gap-2 overflow-hidden">
					<h1 className="shrink-0 whitespace-nowrap text-3xl font-bold leading-tight">
						{job.title} - {job.company.name}
					</h1>
					{!isEditingJob && inlineStatusValue && (
						<Select value={inlineStatusValue} onValueChange={handleInlineStatusChange} disabled={isUpdatingInlineStatus}>
							<SelectTrigger hideChevron className="ml-5 h-auto w-auto shrink-0 gap-2 border-0 bg-transparent px-0 py-0 text-sm font-normal text-gray-600 dark:text-gray-300 shadow-none ring-0 focus:ring-0 focus:ring-offset-0">
								<div className="flex items-center gap-2">
									<div className={`h-2 w-2 rounded-full ${getInlineJobStatusClasses(inlineStatusValue).dot}`} />
									<span className={`text-sm font-normal ${getInlineJobStatusClasses(inlineStatusValue).text}`}>
										{t(`jobs.formWizard.statuses.${inlineStatusValue}`, inlineStatusValue)}
									</span>
									<ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
								</div>
							</SelectTrigger>
							<SelectContent>
								{JOB_STATUS_VALUES.map(status => (
									<SelectItem key={status} value={status}>
										{t(`jobs.formWizard.statuses.${status}`, status)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					{job.published && (
						<Popover>
							<PopoverTrigger asChild>
								<button
									className="flex items-center text-green-600 hover:text-green-700 transition-colors"
									title="Published - Click for options"
									data-tour="job-publish-indicator"
								>
									<Globe className="h-6 w-6" />
								</button>
							</PopoverTrigger>
							<PopoverContent className="w-64 p-3" side="bottom" align="start">
								<div className="space-y-3">
									<div className="flex items-center gap-2 mb-3">
										<Globe className="h-4 w-4 text-green-600" />
										<span className="font-medium text-sm">Published Job</span>
									</div>
									<div className="space-y-2">
										<button
											className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
											onClick={() => {
												window.open(`${window.location.origin}/jobs/${job.id}/public`, '_blank', 'noopener');
											}}
										>
											<Eye className="h-4 w-4 text-blue-500" />
											<div>
												<div className="text-sm font-medium">View Public Page</div>
												<div className="text-xs text-gray-500 dark:text-gray-400">Open in new tab</div>
											</div>
										</button>
										<button
											className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
											onClick={() => {
												navigator.clipboard.writeText(`${window.location.origin}/jobs/${job.id}/public`);
												toast({
													title: 'Copied!',
													description: 'Public page URL copied to clipboard.'
												});
											}}
										>
											<ClipboardCopy className="h-4 w-4 text-gray-600 dark:text-gray-300" />
											<div>
												<div className="text-sm font-medium">Copy Link</div>
												<div className="text-xs text-gray-500 dark:text-gray-400">Share this job publicly</div>
											</div>
										</button>
									</div>
								</div>
							</PopoverContent>
						</Popover>
					)}
				</div>
			</div>

			{/* AI Generated Summary */}
			<div className="w-full mb-6 bg-white dark:bg-gray-800 rounded-sm shadow p-6">
				<h3 className="text-lg font-semibold mb-3">{t("jobs.formWizard.aiSummary")}</h3>
				<p className="text-gray-700 dark:text-gray-200">
					{job.aiSummary || t("jobs.formWizard.aiSummaryPlaceholder")}
				</p>
			</div>			


			{/* Job Profile with Inline Editing */}
			<div className="w-full mb-6" data-tour="job-profile-info">
				<JobProfile
					job={job}
					formattedDate={formattedDate}
					matchesCount={matches.length}
					onChange={async (updatedJob) => {
						//console.log('JobDetail onChange - updatedJob:', updatedJob);
						// Convert the updated job data to the format expected by the update function
						const jobFormData = {
							title: updatedJob.title || '',
							companyId: updatedJob.company?.id || updatedJob.companyId || '',
							companyName: updatedJob.company?.name || '',
							description: updatedJob.description || '',
							aiSummary: updatedJob.aiSummary || '',
							skills: Array.isArray(updatedJob.skills) ? updatedJob.skills.join(', ') : updatedJob.skills || '',
							contactId: updatedJob.contactId,
							status: updatedJob.status || 'open',
							employmentType: updatedJob.employmentType || '',
							timeCommitment: updatedJob.timeCommitment || '',
							contractDuration: updatedJob.contractDuration || '',
							startDate: updatedJob.startDate || '',
							endDate: updatedJob.endDate || '',
							seniorityLevel: updatedJob.seniorityLevel || '',
							budget: updatedJob.budget || '',
							budgetFreelance: updatedJob.budgetFreelance || '',
							locationType: updatedJob.locationType || '',
							workplaceBreakdown: updatedJob.workplaceBreakdown || '',
							hardSkills: Array.isArray(updatedJob.hardSkills) ? updatedJob.hardSkills.join(', ') : updatedJob.hardSkills || '',
							softSkills: Array.isArray(updatedJob.softSkills) ? updatedJob.softSkills.join(', ') : updatedJob.softSkills || '',
							educationLevel: updatedJob.educationLevel || '',
							yearsOfExperience: updatedJob.yearsOfExperience || '',
							languageRequirements: updatedJob.languageRequirements || '',
							extraLang: updatedJob.extraLang || '',
							cultureTags: Array.isArray(updatedJob.cultureTags) ? updatedJob.cultureTags.join(', ') : updatedJob.cultureTags || '',
							applicationDeadline: updatedJob.applicationDeadline || '',
							jobPostingDate: updatedJob.jobPostingDate || '',
							source: updatedJob.source || '',
							assignedRecruiter: updatedJob.assignedRecruiter || '',
							employmentClassification: updatedJob.employmentClassification || '',
							closing_reason: updatedJob.closing_reason || '',
							notes: updatedJob.notes || '',
							custom_fields: updatedJob.custom_fields || {}
						};

						// Call the update function from JobDetailHeader
						const success = await handleUpdateJob(jobFormData);
						if (success) {
							await refetch();
						}
					}}
					isEditing={isEditingJob}
					onEdit={handleJobEdit}
					onSave={handleJobSave}
					onCancel={handleJobCancel}
					customFieldsConfig={customFields}
				/>
			</div>

			{/* Tabs - Full Width */}
			<div className="w-full">				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<DynamicTabBar
						tabs={jobTabConfig}
						pinnedIds={["process", "description", "ai-analysis"]}
						dynamicSlotCount={3}
						defaultDynamicIds={["documents", "assignments", "comments"]}
						activeTabId={activeTab}
						onTabChange={setActiveTab}
						entityType="job"
						userId={user?.id || ""}
					/>

					<TabsContent value="description" className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
								{/* Toolbar for Original tab */}
								<div className="w-full flex items-center justify-end gap-2 mb-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded px-4 py-2">
									{/* Edit/Format buttons */}
									{!isEditingOriginal && (
										<>
											<Button
												className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium"
												variant="default"
												onClick={handleEditOriginalClick}
											>
												<Pencil className="h-4 w-4" />
												{t("jobs.formWizard.editPublicDescription", "Edit")}
											</Button>
											<button
												onClick={handleImproveClick}
												disabled={isFormatting}
												className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition ${
													isFormatting ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-500"
												}`}
											>
												<Wand className="h-4 w-4" />
												{isFormatting ? t("common.loading", "Formatting...") : t("jobs.formWizard.format", "Format")}
											</button>
										</>
									)}
									{isEditingOriginal && (
										<>
											<button
												className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 transition text-sm font-medium"
												onClick={handleSaveOriginal}
												disabled={isSavingOriginal}
											>
												<Check className="h-4 w-4" />
												{isSavingOriginal ? t("common.saving", "Saving...") : t("common.save", "Save")}
											</button>
											<button
												className="flex items-center gap-2 px-3 py-1 rounded bg-gray-400 dark:bg-gray-600 text-white hover:bg-gray-500 dark:hover:bg-gray-500 transition text-sm font-medium"
												onClick={handleCancelEditOriginal}
												disabled={isSavingOriginal}
											>
												<X className="h-4 w-4" />
												{t("common.cancel", "Cancel")}
											</button>
										</>
									)}
								</div>
								<div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
									<h3 className="text-lg font-semibold mb-3">
										{t("jobs.formWizard.jobDescription")}
									</h3>
									{isEditingOriginal ? (
										<div data-color-mode="auto">
											<MDEditor
												value={originalDescDraft}
												onChange={(value) => setOriginalDescDraft(value || "")}
												height={400}
												preview="edit"
												textareaProps={{
													placeholder: t("jobs.formWizard.jobDescriptionPlaceholder", "Enter job description... (Markdown supported)"),
												}}
											/>
										</div>
									) : job.description ? (
										<div className="prose dark:prose-invert max-w-none">
											<ReactMarkdown>{job.description}</ReactMarkdown>
										</div>
									) : (
										<p className="text-gray-500 dark:text-gray-400 italic">{t("jobs.formWizard.noDescription", "No description available for this job.")}</p>
									)}
								</div>

					</TabsContent>

					<TabsContent value="public" className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
						{/* Toolbar for Public tab */}
						<div className="w-full flex items-center justify-end gap-2 mb-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded px-4 py-2">
							{/* Published indicator and public link (only when not editing) */}
							{!isEditingPublic && (
								<>
									{/* Published badge */}
									{job.published ? (
										<span className="inline-flex items-center px-4 py-2 rounded-sm text-sm font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 mr-2">
											<Check className="h-4 w-4 mr-1" /> Published
										</span>
									) : (
										<span className="inline-flex items-center px-4 py-2 rounded-sm text-sm font-semibold bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 mr-2">
											<X className="h-4 w-4 mr-1" /> Unpublished
										</span>
									)}
									{/* Public link button (only if published) */}
									{job.published && (
										<Popover>
											<PopoverTrigger asChild>
												<button
													className="flex items-center gap-2 px-4 py-2 rounded-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition text-sm font-medium mr-2"
													type="button"
												>
													<Eye className="h-4 w-4" />
													View
													<ChevronDown className="h-4 w-4 ml-1" />
												</button>
											</PopoverTrigger>
											<PopoverContent className="w-56 p-2">
												<div className="flex flex-col gap-1">
													{/* Public Page destination */}
													<div className="flex items-center justify-between group hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 py-2 transition">
														<span className="text-blue-700 dark:text-blue-400">Public Page</span>
														<div className="flex items-center ml-2">
															<button
																className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
																onClick={() => {
																	window.open(`${window.location.origin}/jobs/${job.id}/public`, '_blank', 'noopener');
																}}
																type="button"
																aria-label="Open public page"
															>
																<Eye className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" />
															</button>
															<button
																className="ml-1 p-1 rounded hover:bg-gray-200"
																onClick={() => {
																	navigator.clipboard.writeText(`${window.location.origin}/jobs/${job.id}/public`);
																	toast({ title: 'Copied!', description: 'Public page link copied to clipboard.' });
																}}
																type="button"
																aria-label="Copy public page link"
															>
																<ClipboardCopy className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-blue-500" />
															</button>
														</div>
													</div>
													{/* Future destinations can be added here */}
												</div>
											</PopoverContent>
										</Popover>
									)}
								</>
							)}
							{/* Edit/Publish/Save/Cancel buttons */}
							{!isEditingPublic && (
								<>
									<Button
										className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium"
										variant="default"
										onClick={handleEditPublicClick}
									>
										<Pencil className="h-4 w-4" />
										{t("jobs.formWizard.editPublicDescription", "Edit")}
									</Button>
									<Button
										className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium"
										variant="default"
										onClick={() => setShowPublishModal(true)}
									>
										<Send className="h-4 w-4" />
										{t("jobs.formWizard.publishPublicDescription", "Publish")}
									</Button>
								</>
							)}
							{isEditingPublic && (
								<>
									<button
										className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 transition text-sm font-medium"
										onClick={handleSavePublic}
										disabled={isSavingPublic}
									>
										<Check className="h-4 w-4" />
										{isSavingPublic ? t("common.saving", "Saving...") : t("common.save", "Save")}
									</button>
									<button
										className="flex items-center gap-2 px-3 py-1 rounded bg-gray-400 dark:bg-gray-600 text-white hover:bg-gray-500 dark:hover:bg-gray-500 transition text-sm font-medium"
										onClick={handleCancelEditPublic}
										disabled={isSavingPublic}
									>
										<X className="h-4 w-4" />
										{t("common.cancel", "Cancel")}
									</button>
								</>
							)}
						</div>
						<div className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700">
							<h3 className="text-lg font-semibold mb-3 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
								<span>{t("jobs.formWizard.publicDescription", "Public Description")}</span>
								{isEditingPublic && (
									<span className="flex items-center gap-2">
										<button
											className="flex items-center gap-2 px-3 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition text-sm font-medium"
											onClick={() => setPublicDescDraft(marked.parse(job.description || ""))}
										>
											<ClipboardCopy className="h-4 w-4" />
											Copy Original
										</button>
										<button
											className="flex items-center gap-2 px-3 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition text-sm font-medium"
											onClick={() => setShowRewriteModal(true)}
											disabled={rewritingWithAI}
										>
											<Wand className="h-4 w-4" />
											{rewritingWithAI ? 'Rewriting...' : 'Rewrite with AI'}
										</button>
									</span>
								)}
							</h3>
							<div className="p-4">
								{isEditingPublic ? (
									<>
										<CustomQuillToolbar />
										<ReactQuill
											value={publicDescDraft}
											onChange={setPublicDescDraft}
											modules={{
												toolbar: {
													container: '#custom-quill-toolbar'
												}
											}}
											theme="snow"
											className="min-h-[400px]"
										/>
									</>
								) : job.JobDesc_Public && job.JobDesc_Public.trim() ? (
									<div
										className="prose dark:prose-invert max-w-none"
										style={{ fontSize: '1rem !important', lineHeight: '1.5rem !important' }}
										dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.JobDesc_Public) }}
									/>
								) : (
									<div className="text-gray-500 dark:text-gray-400 italic text-base">
										{t("jobs.formWizard.noPublicDescription", "No public description available for this job.")}
									</div>
								)}
							</div>
						</div>
					</TabsContent>

					<TabsContent value="ai-analysis" className="bg-white dark:bg-gray-800 rounded-sm shadow">
						{loadTabContent("ai-analysis") && (
							<Tabs defaultValue="overall" className="w-full">
								<TabsList className="mb-4">
									<TabsTrigger value="overall">Overall</TabsTrigger>
									<TabsTrigger value="profiles">Profiles</TabsTrigger>
								</TabsList>
								<TabsContent value="overall">
									<JobAnalysisTab jobId={id} analysis={analysisData} />
								</TabsContent>								<TabsContent value="profiles">
									<JobProfilesTab profilesData={job.ai_science_analysis} />
								</TabsContent>
							</Tabs>
						)}
					</TabsContent>
					{/* Temporarily hidden - will be re-enabled later */}
					{/* <TabsContent value="matches" className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
						{loadTabContent("matches") && (
							matchesLoading ? (
								<div className="flex justify-center items-center p-6">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
								</div>
							) : matchesError ? (
								<div className="p-6">
									<p className="text-red-500">{t("jobs.formWizard.errorLoadingMatches", { error: matchesError })}</p>
								</div>
							) : (
								matches.length > 0 ? (
									<TopMatchesCard matches={matches} />
								) : (
									<div className="p-6 text-center">
										<p className="text-gray-500 dark:text-gray-400">{t("jobs.formWizard.noMatches", "No matches found for this job.")}</p>
									</div>
								)
							)
						)}
					</TabsContent> */}

					<TabsContent value="documents">
						{loadTabContent("documents") && (
							<JobDocumentsTab jobId={id} />
						)}
					</TabsContent>

					<TabsContent value="tasks">
						{loadTabContent("tasks") && (
							<div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
								<JobTasksTab 
									jobId={id || ''} 
									jobTitle={job.title}
									companyId={job.company.id}
									companyName={job.company.name}
								/>
							</div>
						)}
					</TabsContent>

					<TabsContent value="process">
						{loadTabContent("process") && (
							<div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
								<h3 className="text-lg font-semibold mb-4">{t("candidates.tabs.recruitmentProcess")}</h3>
								<JobKanban jobId={id} skills={job.skills} refreshKey={kanbanRefreshKey} />
							</div>
						)}
					</TabsContent>

					<TabsContent value="assignments">
						{loadTabContent("assignments") && (
							<div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
								<JobAssignmentsTab jobId={id || ''} jobTitle={job.title} />
							</div>
						)}
					</TabsContent>

					<TabsContent value="comments">
						{loadTabContent("comments") && (
							<div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
								<JobComments jobId={id} />
							</div>
						)}
					</TabsContent>

					                                        <TabsContent value="history">
                                                {loadTabContent("history") && (
                                                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                                                                <JobHistory jobId={id} />
                                                        </div>
                                                )}
                                        </TabsContent>
                                        
                                        <TabsContent value="mailbox">
                                                {loadTabContent("mailbox") && (
                                                        <div className="bg-card border border-border rounded-sm p-6">
                                                                <EntityMailbox entityType="job" entityId={id || ''} entityName={job?.title} />
                                                        </div>
                                                )}
                                        </TabsContent>
				</Tabs>
			</div>

			<Dialog open={showRewriteModal} onOpenChange={setShowRewriteModal}>
				<DialogContent className="sm:max-w-[700px] w-[95vw] h-[650px] p-0 overflow-hidden flex flex-col">
					<DialogHeader className="px-6 pt-6 pb-4">
						<DialogTitle className="flex items-center gap-3 text-xl font-semibold">
							<div className="w-10 h-10 rounded-sm bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
								<Sparkles className="h-5 w-5 text-white" />
							</div>
							Rewrite with AI
						</DialogTitle>
						<p className="text-sm text-muted-foreground mt-2">
							Provide instructions to help AI improve your job description
						</p>
					</DialogHeader>
					
					<div className="px-6 pb-6 flex flex-col gap-4">
						<div className="space-y-3">
							<label className="text-sm font-medium text-foreground">
								Your Instructions
							</label>
							<div className="relative">
								<Textarea
									value={rewriteInstructions}
									onChange={e => setRewriteInstructions(e.target.value)}
									placeholder="Tell the AI how to improve your job description&#10;&#10;Examples:&#10;• Make it more engaging and friendly&#10;• Add bullet points for better readability&#10;• Include salary range and benefits&#10;• Use a more professional tone&#10;• Make it shorter and more concise"
									className="min-h-[220px] text-base leading-relaxed resize-none border-2 focus:border-purple-300 focus:ring-purple-200 transition-colors"
								/>
								<div className="absolute bottom-3 right-3 text-xs text-muted-foreground">
									{rewriteInstructions.length}/500
								</div>
							</div>
							{rewriteError && (
								<div className="flex items-center gap-2 text-red-500 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-sm border border-red-200 dark:border-red-800">
									<X className="h-4 w-4 flex-shrink-0" />
									{rewriteError}
								</div>
							)}
						</div>

												{/* Example suggestions */}
						<div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-sm border border-blue-200 dark:border-blue-800">
							<h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">💡 Quick suggestions:</h4>
							<div className="flex flex-wrap gap-2">
								{[
								"Anonymize it",
								"Make it more engaging",
								"Add bullet points",
								"Include benefits",
								"Use friendly tone",
								"Make it concise",
								"Add emojis"
							].map((suggestion) => (
									<button
										key={suggestion}
										onClick={() => setRewriteInstructions(prev => prev.trim() ? `${prev}, ${suggestion.toLowerCase()}` : suggestion)}
										className="px-3 py-1 text-xs bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-500 text-blue-700 dark:text-blue-400 rounded-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
									>
										{suggestion}
									</button>
								))}
							</div>
						</div>
					</div>
					
					<DialogFooter className="px-6 py-4 border-t bg-gray-50/50 dark:bg-gray-800/50 flex-shrink-0">
						<div className="flex justify-between w-full">
							<Button
								variant="ghost"
								onClick={() => setShowRewriteModal(false)}
								disabled={rewritingWithAI}
								className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
							>
								Cancel
							</Button>
							<Button
								onClick={handleSubmitRewrite}
								disabled={rewritingWithAI || !rewriteInstructions.trim()}
								className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 min-w-[100px]"
							>
								{rewritingWithAI ? (
									<div className="flex items-center gap-2">
										<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
										Rewriting...
									</div>
								) : (
									<div className="flex items-center gap-2">
										<Send className="h-4 w-4" />
										Rewrite
									</div>
								)}
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Publish Modal */}
			<Dialog open={showPublishModal} onOpenChange={setShowPublishModal}>
				<DialogContent className="w-[420px] max-w-full animate-pop">
					<DialogHeader>
						<DialogTitle>Publish Job</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-2">
						<div className="flex items-center justify-between">
							<span>Public page</span>
							<Switch
								checked={publishOptions.publicPage}
								disabled={isPublishingJob}
								onCheckedChange={v => setPublishOptions(o => ({ ...o, publicPage: v }))}
							/>
						</div>
						<div className="flex items-center justify-between opacity-60 cursor-not-allowed">
							<Tooltip>
								<TooltipTrigger asChild>
									<div className="flex items-center gap-2 w-full">
										<span>{currentOrganization?.name ? `${currentOrganization.name} website` : "Organization website"}</span>
										<Switch checked={false} disabled />
									</div>
								</TooltipTrigger>
								<TooltipContent>Not Configured</TooltipContent>
							</Tooltip>
						</div>
						<Tooltip>
							<div className="flex items-center justify-between opacity-60 cursor-not-allowed">
								<TooltipTrigger asChild>
									<span className="flex items-center gap-2 select-none">LinkedIn <span className="text-xs bg-gray-200 dark:bg-gray-700 rounded px-2 py-0.5 ml-2">Coming soon</span></span>
								</TooltipTrigger>
								<Switch checked={false} disabled />
								<TooltipContent>Coming soon</TooltipContent>
							</div>
						</Tooltip>
						<Tooltip>
							<div className="flex items-center justify-between opacity-60 cursor-not-allowed">
								<TooltipTrigger asChild>
									<span className="flex items-center gap-2 select-none">Indeed <span className="text-xs bg-gray-200 dark:bg-gray-700 rounded px-2 py-0.5 ml-2">Coming soon</span></span>
								</TooltipTrigger>
								<Switch checked={false} disabled />
								<TooltipContent>Coming soon</TooltipContent>
							</div>
						</Tooltip>
						<Tooltip>
							<div className="flex items-center justify-between opacity-60 cursor-not-allowed">
								<TooltipTrigger asChild>
									<span className="flex items-center gap-2 select-none">Stepstone <span className="text-xs bg-gray-200 dark:bg-gray-700 rounded px-2 py-0.5 ml-2">Coming soon</span></span>
								</TooltipTrigger>
								<Switch checked={false} disabled />
								<TooltipContent>Coming soon</TooltipContent>
							</div>
						</Tooltip>
						<Tooltip>
							<div className="flex items-center justify-between opacity-60 cursor-not-allowed">
								<TooltipTrigger asChild>
									<span className="flex items-center gap-2 select-none">ICT Jobs <span className="text-xs bg-gray-200 dark:bg-gray-700 rounded px-2 py-0.5 ml-2">Coming soon</span></span>
								</TooltipTrigger>
								<Switch checked={false} disabled />
								<TooltipContent>Coming soon</TooltipContent>
							</div>
						</Tooltip>
					</div>
					<DialogFooter className="mt-4">
						<Button
							variant="outline"
							onClick={() => setShowPublishModal(false)}
							type="button"
							disabled={isPublishingJob}
						>
							Cancel
						</Button>
						<Button
							onClick={async () => {
								setIsPublishingJob(true);
								//console.log('[DEBUG] Confirm button clicked for job', id);
								if (publishOptions.publicPage) {
									// Publish
									const { error } = await supabase
										.from("jobs")
										.update({ published: true, publishdate: new Date().toISOString() })
										.eq("id", id);
									if (!error) {
										// Fetch latest job data after update
										const { data: latestJob, error: fetchError } = await supabase
											.from("jobs")
											.select("id, title, JobDesc_Public, organization_uuid")
											.eq("id", id)
											.single();
										//console.log('[DEBUG] Fetched latest job after publish:', latestJob, fetchError);
										if (!fetchError && latestJob) {
											const publicJob = await copyJobToPublicFromJobData(latestJob, currentOrganization?.name, currentOrganization?.id);
											if (publicJob) {
												//console.log('[DEBUG] Published public job:', publicJob);
											}
										}
										setShowPublishModal(false);
										refetch && refetch();
									} else {
										console.error('[DEBUG] Error updating job published status:', error);
									}
								} else {
									// Unpublish
									await supabase
										.from("jobs")
										.update({ published: false, publishdate: null })
										.eq("id", id);
									await supabase
										.from("jobs_public")
										.delete()
										.eq("job_id", id);
									setShowPublishModal(false);
									refetch && refetch();
								}
								setIsPublishingJob(false);
							}}
							disabled={isPublishingJob}
						>
							{isPublishingJob ? "Confirming..." : "Confirm"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{id && job && (
				<JobClosedWonWorkforceModal
					isOpen={showClosedWonModal}
					onClose={async () => {
						setShowClosedWonModal(false);
						// Close remaining open matches when modal is dismissed
						await supabase.rpc('set_match_application_closed', { p_job_id: id });
						setKanbanRefreshKey(k => k + 1);
					}}
					jobId={id}
					jobTitle={job.title}
					onComplete={async () => {
						// Close remaining open matches after workforce creation
						await supabase.rpc('set_match_application_closed', { p_job_id: id });
						refetch();
						setKanbanRefreshKey(k => k + 1);
					}}
				/>
			)}
		</div>
	);
};


function formatTextWithBullets(text) {
	const lines = text.split('\n');
	const elements = [];
	let listItems = [];

	lines.forEach((line, index) => {
		const trimmedLine = line.trim();
		if (/^[-*•]\s+/.test(trimmedLine)) {
			listItems.push(<li key={index}>{trimmedLine.replace(/^[-*•]\s+/, '')}</li>);
		} else {
			if (listItems.length > 0) {
				elements.push(<ul key={`ul-${index}`}>{listItems}</ul>);
				listItems = [];
			}
			if (trimmedLine) {
				elements.push(<p key={index}>{trimmedLine}</p>);
			}
		}
	});

	if (listItems.length > 0) {
		elements.push(<ul key="ul-end">{listItems}</ul>);
	}

	return elements;
}

export default JobDetail;
